import { AssetType, type AssetRef } from "#gen/bundles/v1/bundle_pb.js";
import type { Page } from "#gen/pages/v2/page_pb.js";
import { isPassthroughType } from "#lib/components/flow/viewer/utils.js";
import type { BundleDag } from "#lib/dag.js";
import { getBackgroundImage, parseBackgroundStyleString } from "#lib/theme.js";

// Renderable page keys one hop from the given keys, walking through passthrough nodes like
// decision and experiment to the real page behind them.
export function nextPageKeys(dag: BundleDag<AssetRef>, fromKeys: string[]): string[] {
	const result: Set<string> = new Set();
	const seen: Set<string> = new Set();
	const queue = fromKeys.flatMap(key => dag.getNode(key)?.linksOut.map(link => link.destination) ?? []);
	while (queue.length > 0) {
		const key = queue.shift();
		if (!key || seen.has(key)) {
			continue;
		}
		seen.add(key);
		const node = dag.getNode(key);
		if (!node) {
			continue;
		}
		if (isPassthroughType(node.ref.type)) {
			queue.push(...node.linksOut.map(link => link.destination));
		} else if (node.ref.type !== AssetType.GOTO) {
			// A goto has no destination of its own in the graph and resolves server side to another
			// key, so there is nothing here to warm.
			result.add(key);
		}
	}
	return [...result];
}

// The raw background image URL a page paints. Raw not CDN transformed to match how theme.ts
// sets the CSS var so a prefetch warms the exact bytes the page requests.
export function pageBackgroundUrl(page: Page | undefined): string | undefined {
	if (!page) {
		return undefined;
	}
	return parseBackgroundStyleString(getBackgroundImage(page)).url || undefined;
}
